import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { ActivityTimeline } from '@/features/activity/components/ActivityTimeline';
import { useEntityActivity } from '@/features/activity/hooks/useActivity';
import { formatMoney, PRIORITY_TONES, STAGE_LABELS, STAGE_TONES } from '../constants';
import type { Deal } from '../types';

interface DealDrawerProps {
  deal: Deal | null;
  canDelete: boolean;
  deleting?: boolean;
  onClose: () => void;
  onEdit: (deal: Deal) => void;
  onDelete: (deal: Deal) => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 border-b border-outline py-2 last:border-0">
      <span className="text-body-sm text-on-surface-variant">{label}</span>
      <span className="text-right text-data-md text-on-surface">{value}</span>
    </div>
  );
}

export function DealDrawer({ deal, canDelete, deleting, onClose, onEdit, onDelete }: DealDrawerProps) {
  const { data: activity = [], isLoading: activityLoading, isError: activityError } = useEntityActivity('deal', deal?.id ?? null);
  return (
    <Drawer open={!!deal} onClose={onClose} title={deal?.title ?? 'Deal'}>
      {deal && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={STAGE_TONES[deal.stage] ?? 'neutral'}>{STAGE_LABELS[deal.stage] ?? deal.stage}</Badge>
            {deal.priority && <Badge tone={PRIORITY_TONES[deal.priority] ?? 'neutral'}>{deal.priority}</Badge>}
            {typeof deal.ai_score === 'number' && (
              <Badge tone={deal.ai_score >= 70 ? 'success' : deal.ai_score >= 40 ? 'warning' : 'neutral'}>
                AI {deal.ai_score}%
              </Badge>
            )}
          </div>

          <div>
            <Row label="Value" value={formatMoney(deal.value, deal.currency)} />
            <Row label="Probability" value={deal.probability != null ? `${deal.probability}%` : null} />
            <Row label="Expected Close" value={deal.expected_close} />
            <Row label="Owner" value={deal.assigned_name} />
            <Row label="Created By" value={deal.creator_name} />
          </div>

          {/* Contact details — integration point for Contacts / Lead conversion */}
          {(deal.contact_name || deal.business_name || deal.phone || deal.email || deal.service_required) && (
            <div>
              <h3 className="mb-1 text-label-caps uppercase text-on-surface-variant">Contact</h3>
              <Row label="Name" value={deal.contact_name} />
              <Row label="Business" value={deal.business_name} />
              <Row label="Phone" value={deal.phone} />
              <Row label="Email" value={deal.email} />
              <Row label="Service" value={deal.service_required} />
            </div>
          )}

          {deal.notes && (
            <div>
              <h3 className="mb-1 text-label-caps uppercase text-on-surface-variant">Notes</h3>
              <p className="whitespace-pre-wrap text-data-md text-on-surface">{deal.notes}</p>
            </div>
          )}

          <div>
            <h3 className="mb-2 text-label-caps uppercase text-on-surface-variant">Activity</h3>
            <ActivityTimeline
              items={activity}
              isLoading={activityLoading}
              isError={activityError}
              showActor
              emptyTitle="No activity yet"
              emptyDescription="Stage changes, updates and comments will appear here."
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={() => onEdit(deal)} className="flex-1">
              Edit Deal
            </Button>
            {canDelete && (
              <Button variant="danger" loading={deleting} onClick={() => onDelete(deal)}>
                Delete
              </Button>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
